import { useEffect, useRef, useSyncExternalStore } from 'react';

// Phone detection: body classes let the CSS tune layout per device (phone, iOS, Android, installed app).
export const PHONE_QUERY = '(max-width: 760px), (max-width: 1024px) and (max-height: 500px) and (pointer: coarse)';
export const isPhone = () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches;
const subscribePhone=(update:()=>void)=>{const media=matchMedia(PHONE_QUERY);media.addEventListener('change',update);return()=>media.removeEventListener('change',update)};
export const usePhoneLayout=()=>useSyncExternalStore(subscribePhone,isPhone,()=>false);
function tagDevice() {
  const ua = navigator.userAgent, b = document.body.classList;
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  b.toggle('is-phone', isPhone());
  b.toggle('is-touch', matchMedia('(pointer: coarse)').matches);
  b.toggle('is-ios', ios);
  b.toggle('is-android', /Android/.test(ua));
  b.toggle('is-standalone', matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true);
}
if (typeof window !== 'undefined') { tagDevice(); window.addEventListener('resize', tagDevice); }

type Options = {
  enabled: boolean;
  sidebar: boolean; setSidebar: (v: boolean) => void;
  panel: boolean; openPanel: () => void; closePanel: () => void; canOpenPanel: boolean;
};

const IGNORE = 'input,textarea,select,[contenteditable=true],video,.voice-media,[role=dialog],.modal,.lightbox,.call-full,.e2ee-backdrop,.qr-approve-backdrop,.emoji-picker,.context-menu,[data-no-swipe]';
function scrollsSideways(el: HTMLElement | null) {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    if (n.scrollWidth > n.clientWidth + 4) { const o = getComputedStyle(n).overflowX; if (o === 'auto' || o === 'scroll') return true; }
  }
  return false;
}

// Discord-style swipes on phones: swipe right opens servers + channels, swipe left opens the member list / profile.
// The drawer follows the finger and snaps open or closed on release.
export function useMobileGestures(opts: Options) {
  const o = useRef(opts); o.current = opts;
  useEffect(() => {
    let sx = 0, sy = 0, st = 0, mode: '' | 'none' | 'open-left' | 'close-left' | 'close-right' | 'open-right' = '', dx = 0, width = 0;
    const els = () => [document.querySelector<HTMLElement>('.app-shell>.server-rail'), document.querySelector<HTMLElement>('.app-shell>.channel-sidebar')].filter(Boolean) as HTMLElement[];
    const panel = () => document.querySelector<HTMLElement>('.app-shell .members-panel,.app-shell .dm-profile-panel');
    const setX = (list: HTMLElement[], x: number | null) => list.forEach(el => { if(x===null){el.style.removeProperty('transition');el.style.removeProperty('transform')}else{el.style.setProperty('transition','none','important');el.style.setProperty('transform',`translateX(${x}px)`,'important')} });
    const start = (e: TouchEvent) => {
      mode = '';
      if (!o.current.enabled || e.touches.length !== 1 || !isPhone()) { mode = 'none'; return; }
      const t = e.touches[0], target = e.target as HTMLElement;
      if (target?.closest?.(IGNORE) || scrollsSideways(target)) { mode = 'none'; return; }
      sx = t.clientX; sy = t.clientY; st = performance.now(); dx = 0;
    };
    const move = (e: TouchEvent) => {
      if (mode === 'none') return;
      if (document.body.classList.contains('touch-dragging')) { if (mode) { document.body.classList.remove('drawer-dragging'); setX(els(), null); } mode = 'none'; return; }
      const t = e.touches[0]; if (!t) return;
      const mx = t.clientX - sx, my = t.clientY - sy;
      if (!mode) {
        if (Math.abs(my) > 12 && Math.abs(my) > Math.abs(mx)) { mode = 'none'; return; }
        if (Math.abs(mx) < 12 || Math.abs(mx) < Math.abs(my) * 1.3) return;
        const { sidebar, panel: p, canOpenPanel } = o.current;
        mode = sidebar ? (mx < 0 ? 'close-left' : 'none') : p ? (mx > 0 ? 'close-right' : 'none') : mx > 0 ? 'open-left' : canOpenPanel ? 'open-right' : 'none';
        if (mode === 'none') return;
        const list = els(); width = list.reduce((w, el) => w + el.offsetWidth, 0) || 300;
        if (mode === 'close-right') width = panel()?.offsetWidth || 300;
        document.body.classList.add('drawer-dragging');
      }
      dx = mx;
      if (e.cancelable) e.preventDefault();
      if (mode === 'open-left') setX(els(), Math.min(0, -width + dx));
      else if (mode === 'close-left') setX(els(), Math.max(-width, Math.min(0, dx)));
      else if (mode === 'close-right') { const p = panel(); if (p) setX([p], Math.max(0, dx)); }
    };
    const end = () => {
      if (!mode || mode === 'none') { mode = ''; return; }
      const v = dx / Math.max(1, performance.now() - st), far = Math.abs(dx) > width * 0.35, fast = Math.abs(v) > 0.45;
      const m = mode; mode = '';
      document.body.classList.remove('drawer-dragging');
      setX(els(), null); const p = panel(); if (p) setX([p], null);
      if (m === 'open-left' && dx > 0 && (far || fast)) o.current.setSidebar(true);
      else if (m === 'close-left' && dx < 0 && (far || fast)) o.current.setSidebar(false);
      else if (m === 'close-right' && dx > 0 && (far || fast)) o.current.closePanel();
      else if (m === 'open-right' && dx < -50) o.current.openPanel();
    };
    const cancel=()=>{mode='';document.body.classList.remove('drawer-dragging');setX(els(),null);const p=panel();if(p)setX([p],null);};
    window.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', end, { passive: true });
    window.addEventListener('touchcancel', cancel, { passive: true });
    return () => { window.removeEventListener('touchstart', start); window.removeEventListener('touchmove', move); window.removeEventListener('touchend', end); window.removeEventListener('touchcancel', cancel);cancel(); };
  }, []);
}
